import { useCallback, useEffect, useState } from "react";
import { Trash2, Ban } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatDate, formatTime } from "@/lib/format";
import HoursEditor from "@/components/HoursEditor";
import { Switch } from "@/components/ui/switch";

function BlockedTimes() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ date: "", all_day: true, start_time: "09:00", end_time: "12:00", reason: "" });
  const load = useCallback(() => api.get("/provider/blocked").then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!form.date) return toast.error("Choose a date");
    const payload = form.all_day ? { date: form.date, all_day: true, reason: form.reason } : form;
    try { await api.post("/provider/blocked", payload); toast.success("Time blocked"); setForm({ ...form, reason: "" }); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => { await api.delete(`/provider/blocked/${id}`); load(); };
  const input = "h-11 rounded-xl border border-slate-200 px-3 text-sm";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="blocked-times">
      <h3 className="flex items-center gap-2 font-heading text-lg font-semibold"><Ban className="h-5 w-5 text-rose-500" />Block unavailable dates / times</h3>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <input type="date" data-testid="block-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={input} />
        <label className="flex h-11 items-center gap-2 text-sm"><Switch data-testid="block-all-day" checked={form.all_day} onCheckedChange={(v) => setForm({ ...form, all_day: v })} />All day</label>
        {!form.all_day && <>
          <input type="time" data-testid="block-start" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className={input} />
          <input type="time" data-testid="block-end" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className={input} />
        </>}
        <input placeholder="Reason (optional)" data-testid="block-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className={`${input} flex-1 min-w-[160px]`} />
        <button data-testid="block-add" onClick={add} className="h-11 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white">Block</button>
      </div>
      <ul className="mt-4 divide-y divide-slate-100">
        {items.length === 0 && <li className="py-3 text-sm text-slate-500">No blocked times.</li>}
        {items.map((b) => (
          <li key={b.id} className="flex items-center justify-between py-3 text-sm" data-testid={`blocked-${b.id}`}>
            <span><b>{formatDate(b.date)}</b> · {b.all_day ? "All day" : `${formatTime(b.start_time)} - ${formatTime(b.end_time)}`} {b.reason && <span className="text-slate-500">({b.reason})</span>}</span>
            <button data-testid={`unblock-${b.id}`} onClick={() => remove(b.id)} className="rounded-lg p-2 text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ProviderAvailability({ provider, onSaved }) {
  const [hours, setHours] = useState(provider.working_hours);
  const [interval, setIntervalVal] = useState(provider.slot_interval || 30);
  const save = async () => {
    try { await api.put("/provider/availability", { working_hours: hours, slot_interval: Number(interval) }); toast.success("Availability saved"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6" data-testid="provider-availability">
      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-xl font-semibold">Working days & hours</h2>
          <label className="flex items-center gap-2 text-sm text-slate-600">Slot interval
            <select data-testid="slot-interval" value={interval} onChange={(e) => setIntervalVal(e.target.value)} className="h-10 rounded-lg border border-slate-200 px-2">
              {[15, 30, 45, 60].map((m) => <option key={m} value={m}>{`${m} min`}</option>)}
            </select>
          </label>
        </div>
        <HoursEditor value={hours} onChange={setHours} />
        <button data-testid="save-availability" onClick={save} className="mt-4 h-11 rounded-xl bg-sky-600 px-5 text-sm font-semibold text-white hover:bg-sky-700">Save availability</button>
      </div>
      <BlockedTimes />
    </div>
  );
}
